<template>
  <el-drawer
    class="edi-detail-drawer"
    :visible.sync="visible"
    direction="rtl"
    size="680px"
    :modal-append-to-body="false"
    :wrapper-closable="false"
    :before-close="handleClose"
  >
    <template slot="title">
      <div class="drawer-title">{{ detailData.orderNo }}</div>
    </template>

    <div class="drawer-body">
      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-label">{{ $t('ExternalOrders.EDI.detailCurrentStep') }}</div>
          <el-tag size="mini" :type="isException ? 'danger' : 'success'">{{ $t(filterList.bjGet(detailData.statusLabel)) }}</el-tag>
          <div v-if="isException && exceptionReason" class="exception-reason">
            <span>{{ exceptionReason }}</span>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-label">{{ $t('ExternalOrders.EDI.detailActionOwner') }}</div>
          <div class="summary-value">{{ detailData.actionOwner }}</div>
        </div>
      </div>

      <BjDivider size="mini">{{ $t('ExternalOrders.EDI.detailOrderNumbers') }}</BjDivider>
      <dl class="info-list">
        <dt>{{ $t('ExternalOrders.EDI.detailRetailerPo') }}</dt>
        <dd>{{ detailData.orderNo || '——' }}</dd>
        <dt>{{ $t('ExternalOrders.EDI.detailClientErpNo') }}</dt>
        <dd>{{ detailData.orderNoErp || '——' }}</dd>
        <dt>{{ $t('ExternalOrders.EDI.detailCsgOrderNo') }}</dt>
        <dd>{{ detailData.orderNoWms || '——' }}</dd>
        <dt>{{ $t('ExternalOrders.EDI.columnStore') }}</dt>
        <dd>{{ detailData.erpStoreName || '——' }}</dd>
        <dt>{{ $t('ExternalOrders.EDI.columnReceived') }}</dt>
        <dd class="info-time">{{ detailData.createdDate | timeFilters }}</dd>
      </dl>

      <BjDivider v-if="hasHistories" size="mini">{{ $t('ExternalOrders.EDI.detailProcessingTimeline') }}</BjDivider>
      <div v-if="hasHistories" class="timeline-list">
        <div v-for="(item, index) in detailData.histories" :key="index" class="timeline-item">
          <span class="timeline-index">{{ index + 1 }}</span>
          <span class="timeline-label">{{ item.message }}</span>
          <span class="timeline-time">{{ item.createdDate | timeFilters }}</span>
        </div>
      </div>

      <BjDivider v-if="hasShippingLocation" size="mini">{{ $t('ExternalOrders.EDI.detailDeliveryAddress') }}</BjDivider>
      <div v-if="hasShippingLocation" class="address-card">
        <div class="address-field">
          <div class="address-label">{{ $t('ExternalOrders.EDI.detailRecipient') }}</div>
          <div class="address-value">{{ deliveryAddress.recipient }}</div>
        </div>
        <div class="address-field">
          <div class="address-label">{{ $t('ExternalOrders.EDI.detailPhone') }}</div>
          <div class="address-value">{{ deliveryAddress.phone }}</div>
        </div>
        <div class="address-field address-field-full">
          <div class="address-label">{{ $t('ExternalOrders.EDI.detailAddress') }}</div>
          <div class="address-value">{{ deliveryAddress.address }}</div>
        </div>
        <div class="address-field">
          <div class="address-label">{{ $t('ExternalOrders.EDI.detailSuburb') }}</div>
          <div class="address-value">{{ deliveryAddress.suburb }}</div>
        </div>
        <div class="address-field">
          <div class="address-label">{{ $t('ExternalOrders.EDI.detailStatePostcode') }}</div>
          <div class="address-value">{{ deliveryAddress.statePostcode }}</div>
        </div>
        <div class="address-field">
          <div class="address-label">{{ $t('ExternalOrders.EDI.detailCountry') }}</div>
          <div class="address-value">{{ deliveryAddress.country }}</div>
        </div>
      </div>

      <BjDivider size="mini">{{ $t('ExternalOrders.EDI.detailOrderLines') }}</BjDivider>
      <el-tabs v-model="activeTab" class="order-lines-tabs">
        <el-tab-pane :label="$t('ExternalOrders.EDI.detailItems')" name="items">
          <el-table :data="detailData.lines" size="mini" border>
            <el-table-column prop="upcCode" :label="$t('ExternalOrders.EDI.detailSku')" width="240" align="center" />
            <el-table-column prop="quantity" :label="$t('ExternalOrders.EDI.detailQty')" width="100" align="center" />
            <el-table-column prop="status" :label="$t('ExternalOrders.EDI.detailMatch')" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.status == 1" size="mini" type="success">{{ $t('ExternalOrders.EDI.MatchStatus1') }}</el-tag>
                <el-tag v-if="scope.row.status == 0" size="mini" type="danger">{{ $t('ExternalOrders.EDI.MatchStatus2') }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane v-if="!$store.getters.cusId" name="payload">
          <template slot="label">
            <span>{{ $t('ExternalOrders.EDI.detailEdiPayload') }}</span>
            <span class="tab-tips">{{ $t('ExternalOrders.EDI.detailInternalOnly') }}</span>
          </template>
          <el-input
            v-model="ediPayload"
            type="textarea"
            :rows="9"
            :disabled="true"
            resize="none"
          />
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="drawer-footer">
      <el-button v-if="detailData.statusLabel == 'exception'" size="mini" @click="onRetry">{{ $t('ExternalOrders.page.retryBtn') }}</el-button>
      <el-button size="mini" @click="handleClose">{{ $t('ExternalOrders.EDI.detailClose') }}</el-button>
      <el-button v-if="canApprove" type="primary" size="mini" @click="onApprove">{{ $t('ExternalOrders.EDI.detailApproveAndContinue') }}</el-button>
    </div>
  </el-drawer>
</template>

<script>
import { waveApi } from '@/api';

export default {
  name: 'EdiDetailDrawer',
  data() {
    return {
      visible: false,
      activeTab: 'items',
      detailData: {},
      ediPayload: '',
      canApprove: false,
      filterList: [
        { label: this.$t('ExternalOrders.EDI.summaryException'), value: 'exception' },
        { label: this.$t('ExternalOrders.EDI.summaryAwaitingClientApproval'), value: 'pending' },
        { label: this.$t('ExternalOrders.EDI.summaryInProgress'), value: 'inprogress' },
        { label: this.$t('ExternalOrders.EDI.summaryComplete'), value: 'completed' },
      ],
    };
  },
  computed: {
    isException() {
      return this.detailData.statusLabel === 'exception';
    },
    exceptionReason() {
      return this.detailData.statusDesc || '';
    },
    hasHistories() {
      return Array.isArray(this.detailData.histories) && this.detailData.histories.length;
    },
    shippingLocation() {
      return (this.detailData.shippingInfo && this.detailData.shippingInfo.location) || {};
    },
    deliveryAddress() {
      const location = this.shippingLocation;
      const statePostcode = [location.state, location.postcode].filter(Boolean).join(' ');

      return {
        recipient: location.partyName || location.storeName || '',
        phone: location.phone || '',
        address: location.address || '',
        suburb: location.suburb || '',
        statePostcode,
        country: location.country || '',
      };
    },
    hasShippingLocation() {
      return Object.keys(this.deliveryAddress).some(key => !!this.deliveryAddress[key]);
    },
  },
  methods: {
    show(row) {
      this.canApprove = !!row.isShowApproveBtn;
      waveApi.getEDIPageDetail({ code: row.erpStoreCode, id: row.docId }).then(res => {
        if (res.orderNo) {
          this.detailData = res || {};
          this.activeTab = 'items';
          this.visible = true;
        }
      });
      waveApi.getEDIPageDetailPayload({ code: row.erpStoreCode, id: row.docId }).then(res => {
        this.ediPayload = res.content || '';
      });
    },
    onRetry() {
      waveApi.retryEDIPageData({ code: this.detailData.erpStoreCode, id: this.detailData.docId, status: 'ACCEPTED' }).then(res => {
        if (!res.failed) {
          this.$message.success(this.$t('toast.operationSuccessText'));
          setTimeout(() => {
            this.handleClose();
            this.$emit('refresh');
          }, 500);
        }
      });
    },
    onApprove() {
      this.$confirm(this.$t('toast.operationText'), this.$t('toast.hintText'), {
        confirmButtonText: this.$t('toast.okText'),
        cancelButtonText: this.$t('toast.cancelText'),
        type: 'warning',
      }).then(() => {
        waveApi.EDIPageApprove({
          code: this.detailData.erpStoreCode,
          id: this.detailData.docId,
        }).then(res => {
          if (res.orderStatus === 'CSG in progress') {
            this.$message.success(this.$t('toast.operationSuccessText'));
            setTimeout(() => {
              this.handleClose();
              this.$emit('refresh');
            }, 500);
          }
        });
      }).catch(() => {});
    },
    handleClose(done) {
      this.visible = false;
      this.canApprove = false;
      if (typeof done === 'function') {
        done();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.edi-detail-drawer ::v-deep .el-drawer__header {
  margin-bottom: 0;
  padding: 18px 24px 14px;
  border-bottom: 1px solid #ebeef5;
}

.edi-detail-drawer ::v-deep .el-drawer__body {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-title {
  color: #303133;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.drawer-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 24px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.summary-card {
  min-height: 64px;
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafbfc;
}

.summary-label {
  margin-bottom: 8px;
  color: #909399;
  font-size: 12px;
}

.summary-value {
  color: #606266;
  font-size: 13px;
}

.exception-reason {
  margin-top: 8px;
  color: #606266;
  font-size: 12px;
  line-height: 18px;
  word-break: break-word;
}

::v-deep .bj-divider {
  padding: 10px 0;
  margin-bottom: 8px;
}

.info-list {
  display: grid;
  grid-template-columns: 140px 1fr;
  row-gap: 8px;
  margin: 0 0 10px;
  color: #606266;
  font-size: 14px;

  dt {
    color: #909399;
    font-weight: normal;
  }

  dd {
    margin: 0;
  }
}

.timeline-list {
  margin-bottom: 18px;
}

.timeline-item {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 150px;
  align-items: center;
  column-gap: 10px;
  min-height: 40px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.timeline-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #67c23a;
  color: #fff;
  font-size: 12px;
  line-height: 22px;
}

.timeline-time {
  color: #c0c4cc;
  font-size: 12px;
  font-weight: normal;
  text-align: right;
  white-space: nowrap;
}

.info-time {
  white-space: nowrap;
}

.address-card {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 26px;
  margin-bottom: 14px;
  padding: 14px 12px;
  border: 1px solid #d9e2ff;
  background: #fafbff;
}

.address-field {
  min-width: 0;
}

.address-field-full {
  grid-column: 1 / -1;
}

.address-label {
  margin-bottom: 6px;
  color: #7a86a8;
  font-size: 12px;
  line-height: 16px;
}

.address-value {
  min-height: 18px;
  color: #001f4d;
  font-size: 13px;
  line-height: 18px;
  word-break: break-word;
}

.order-lines-tabs {
  ::v-deep .el-tabs__header {
    margin-bottom: 10px;
  }
}

.tab-tips {
  margin-left: 4px;
  color: #c0c4cc;
  font-size: 12px;
}

.drawer-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding: 14px 24px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}
</style>
